import React, { useState, useRef, useEffect } from "react";
import CheckBoxChecked from "../icons/CheckBoxChecked";
import CheckBoxUnChecked from "../icons/CheckBoxUnChecked";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

interface WvMultiSelectProps {
  name: string;
  options: { name: string; value: string }[];
  selectedValues?: string[];
  onSelectionChange?: (selectedValues: string[]) => void;
  className?: string;
  label?: string;
  disabled?: boolean;
}

const WvMultiSelect: React.FC<WvMultiSelectProps> = ({
  name,
  options,
  selectedValues,
  onSelectionChange,
  className = "",
  label,
  disabled = false,
}) => {
  const { $pt } = usePrismicLabels();
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelected, setInternalSelected] = useState<string[]>([]);
  const [pendingSelected, setPendingSelected] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = selectedValues !== undefined;
  const selected = isControlled ? (selectedValues || []) : internalSelected;

  // Initialize pending selections when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPendingSelected(selected);
    }
  }, [isOpen, selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset pending selections when closing without applying
        setPendingSelected(selected);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, selected]);

  const toggleOption = (value: string) => {
    const newPending = pendingSelected.includes(value)
      ? pendingSelected.filter((v) => v !== value)
      : [...pendingSelected, value];
    
    setPendingSelected(newPending);
  };

  const handleApply = () => {
    if (isControlled) {
      // In controlled mode, call the callback with pending selections
      onSelectionChange?.(pendingSelected);
    } else {
      // In uncontrolled mode, update internal state
      setInternalSelected(pendingSelected);
      onSelectionChange?.(pendingSelected);
    }
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (selected.length === 0) {
      return $pt("multiselect_placeholder");
    }
    return options
      .filter(option => selected.includes(option.value))
      .map(option => option.name)
      .join(", ");
  };

  return (
    <div className={`mb-3 multi-select ${className}`} ref={dropdownRef}>
      {label && (
        <div className="input input--label">
          <span className="input__label">{label}</span>
        </div>
      )}
      <div className="position-relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="multi-select--toggle"
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <span className="multi-select--placeholder">
            {getDisplayText()}
          </span>
          <span
            data-icon-after={isOpen ? "" : ""}
            style={{ marginLeft: "auto" }}
          ></span>
        </button>

        {isOpen && (
          <div
            className="multi-select--dropdown"
            role="listbox"
          >
            <ul className="multi-select--dropdown-list filters__filter-options py-2 mb-0">
              {options.map((option) => {
                const isSelected = pendingSelected.includes(option.value);
                return (
                  <li key={`${name}_${option.value}`} role="option">
                    <label className="input input--checkbox filters__option">
                      <div className="d-flex align-items-center no-gutters">
                        <input
                          type="checkbox"
                          name={option.value}
                          checked={isSelected}
                          className="input__field checked"
                          onChange={(e) => {
                            toggleOption(option.value);
                          }}
                        />
                        <div className="col-auto" data-icon-before="">
                          {isSelected ? (
                            <CheckBoxChecked width={24} height={24} />
                          ) : (
                            <CheckBoxUnChecked width={24} height={24} stroke="#000" />
                          )}
                        </div>
                        <div className="col d-flex align-items-center pl-2 pb-1">
                          <span className="input__checkbox-label">
                            {option.name}
                          </span>
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="multi-select--dropdown-footer">
              <button onClick={handleApply}>
                <span>{$pt("multiselect_apply_filter")}</span>
                <span data-icon-after=""></span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WvMultiSelect;

